import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, collection, query, where, getDocs, doc, updateDoc, getDoc, writeBatch } from "firebase/firestore";
import app from "./firebase";
import styles from "./AdminDashboard.module.css";

const auth = getAuth(app);
const db = getFirestore(app);

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [newUsers, setNewUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState("");

  const toJsDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "object" && typeof value.seconds === "number") return new Date(value.seconds * 1000);
    if (typeof value?.toDate === "function") return value.toDate();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const toYearMonth = (date) => {
    const d = toJsDate(date) || new Date(0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const monthShort = (date) => {
    const d = toJsDate(date) || new Date(0);
    return d.toLocaleString("en-US", { month: "short" });
  };

  const stableEventSort = (a, b) => {
    const ad = toJsDate(a?.date) || a?.dateObj || new Date(0);
    const bd = toJsDate(b?.date) || b?.dateObj || new Date(0);
    const adt = ad.getTime();
    const bdt = bd.getTime();
    if (adt !== bdt) return adt - bdt;

    const ac = toJsDate(a?.createdAt) || ad;
    const bc = toJsDate(b?.createdAt) || bd;
    const act = ac.getTime();
    const bct = bc.getTime();
    if (act !== bct) return act - bct;

    return String(a?.id || "").localeCompare(String(b?.id || ""));
  };

  // Unit conversion (same categories as ProductDetails)
  const UNIT_CONVERSIONS = {
    g: 1,
    kg: 1000,
    lbs: 453.592,
    lb: 453.592,
    oz: 28.3495,
    ml: 1,
    liters: 1000,
    l: 1000,
    cups: 236.588,
    cup: 236.588,
    tbsp: 14.7868,
    tsp: 4.92892,
    units: 1,
    pieces: 1,
    pcs: 1,
    boxes: 1,
    packets: 1,
    bottles: 1,
    cans: 1,
    bags: 1
  };

  const UNIT_CATEGORIES = {
    weight: ["g", "kg", "lbs", "lb", "oz"],
    volume: ["ml", "liters", "l", "cups", "cup", "tbsp", "tsp"],
    count: ["units", "pieces", "pcs", "boxes", "packets", "bottles", "cans", "bags"]
  };

  const getUnitCategory = (unit) => {
    const u = String(unit || "").toLowerCase();
    for (const [category, units] of Object.entries(UNIT_CATEGORIES)) {
      if (units.includes(u)) return category;
    }
    return null;
  };

  const convertUnits = (value, fromUnit, toUnit) => {
    const from = String(fromUnit || "").toLowerCase();
    const to = String(toUnit || "").toLowerCase();
    if (!from || !to || from === to) return value;
    const fromCat = getUnitCategory(from);
    const toCat = getUnitCategory(to);
    if (!fromCat || !toCat || fromCat !== toCat) return value;
    const fromFactor = UNIT_CONVERSIONS[from];
    const toFactor = UNIT_CONVERSIONS[to];
    if (!fromFactor || !toFactor) return value;
    const base = Number(value) * fromFactor;
    return base / toFactor;
  };

  const runFifoMigration = async () => {
    const ok = window.confirm(
      "This will backfill batch codes and create FIFO lots + usage history for existing production events.\n\nRun once. Continue?"
    );
    if (!ok) return;

    setMigrationRunning(true);
    setMigrationStatus("Fetching existing data...");

    try {
      const [ingredientsSnap, purchaseSnap, lotsSnap, eventsSnap] = await Promise.all([
        getDocs(collection(db, "ingredients")),
        getDocs(collection(db, "ingredient_purchase_history")),
        getDocs(collection(db, "ingredient_inventory_lots")),
        getDocs(collection(db, "productionEvents"))
      ]);

      const ingredients = ingredientsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const purchaseHistory = purchaseSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const existingLots = lotsSnap.docs.map(d => ({ id: d.id, ref: d.ref, ...d.data() }));
      const productionEvents = eventsSnap.docs.map(d => ({ id: d.id, ref: d.ref, ...d.data() }));

      const ingredientByName = new Map();
      const ingredientById = new Map();
      ingredients.forEach(i => {
        ingredientById.set(i.id, i);
        if (i?.name) ingredientByName.set(String(i.name).toLowerCase(), i);
      });

      const existingPurchaseHistoryIds = new Set(
        existingLots.map(l => l.purchaseHistoryId).filter(Boolean)
      );

      // Prepare in-memory lots by ingredientId
      const lotsByIngredientId = new Map();
      const normalizeLot = (lot) => {
        const pd = toJsDate(lot.purchaseDate) || new Date(0);
        return {
          ...lot,
          purchaseDate: pd,
          remainingQuantity: Number(lot.remainingQuantity || 0),
          quantityPurchased: Number(lot.quantityPurchased || 0)
        };
      };

      existingLots.forEach(l => {
        const ingId = l.ingredientId;
        if (!ingId) return;
        if (!lotsByIngredientId.has(ingId)) lotsByIngredientId.set(ingId, []);
        lotsByIngredientId.get(ingId).push(normalizeLot(l));
      });

      for (const [, lots] of lotsByIngredientId.entries()) {
        lots.sort((a, b) => a.purchaseDate.getTime() - b.purchaseDate.getTime());
      }

      // 1) Create missing lots from purchase history
      const lotOps = [];
      let createdLots = 0;
      purchaseHistory.forEach(ph => {
        const qty = Number(ph.quantity);
        if (!ph.ingredientId || !Number.isFinite(qty) || qty <= 0) return;
        if (existingPurchaseHistoryIds.has(ph.id)) return;

        const purchaseDate = toJsDate(ph.date) || new Date();
        const lotRef = doc(collection(db, "ingredient_inventory_lots"));

        const storeName = ph.storeName || null;
        const ingredientName = ph.ingredientName || ingredientById.get(ph.ingredientId)?.name || null;

        lotOps.push((batch) => {
          batch.set(lotRef, {
            ingredientId: ph.ingredientId,
            ingredientName,
            unit: ph.unit || ingredientById.get(ph.ingredientId)?.unit || null,
            storeId: ph.storeId || ph.purchaseStoreId || null,
            storeName,
            purchaseHistoryId: ph.id,
            purchaseDate,
            quantityPurchased: qty,
            remainingQuantity: qty,
            costPerBaseUnit: ph.costPerBaseUnit ?? null,
            totalPrice: ph.totalPrice ?? null,
            pricePerLb: ph.pricePerLb ?? null,
            packageCount: ph.packageCount ?? null,
            packageSize: ph.packageSize ?? null,
            packageUnit: ph.packageUnit || null,
            costPerPackage: ph.costPerPackage ?? null,
            createdAt: new Date(),
            updatedAt: new Date(),
            isLegacy: false
          });
        });

        const lotObj = normalizeLot({
          id: lotRef.id,
          ref: lotRef,
          ingredientId: ph.ingredientId,
          ingredientName,
          unit: ph.unit || ingredientById.get(ph.ingredientId)?.unit || null,
          purchaseHistoryId: ph.id,
          purchaseDate,
          remainingQuantity: qty,
          quantityPurchased: qty,
          isLegacy: false
        });

        if (!lotsByIngredientId.has(ph.ingredientId)) lotsByIngredientId.set(ph.ingredientId, []);
        lotsByIngredientId.get(ph.ingredientId).push(lotObj);
        createdLots += 1;
      });

      // Re-sort after adding new lots
      for (const [, lots] of lotsByIngredientId.entries()) {
        lots.sort((a, b) => a.purchaseDate.getTime() - b.purchaseDate.getTime());
      }

      const commitOps = async (ops, label) => {
        const CHUNK = 450;
        let committed = 0;
        for (let i = 0; i < ops.length; i += CHUNK) {
          const batch = writeBatch(db);
          ops.slice(i, i + CHUNK).forEach(op => op(batch));
          await batch.commit();
          committed += Math.min(CHUNK, ops.length - i);
          setMigrationStatus(`${label} (${committed}/${ops.length})...`);
        }
      };

      if (lotOps.length > 0) {
        setMigrationStatus(`Creating missing lots from purchase history (${lotOps.length})...`);
        await commitOps(lotOps, "Creating lots");
      }

      // 2) Recompute batch codes for production events (timestamp-ordered within product+month)
      setMigrationStatus("Recomputing batch codes (timestamp-based)...");

      const eventsWithDates = productionEvents
        .map(e => ({
          ...e,
          dateObj: toJsDate(e.date) || new Date(0),
          createdObj: toJsDate(e.createdAt) || null
        }))
        .sort(stableEventSort);

      const groupKey = (e) => {
        const evtDate = e.dateObj || toJsDate(e.date) || new Date(0);
        const ym = e.yearMonth || toYearMonth(evtDate);
        return `${e.productId || ""}::${ym}`;
      };

      const groups = new Map();
      eventsWithDates.forEach(e => {
        const key = groupKey(e);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(e);
      });

      const computedByEventId = new Map();
      for (const [, group] of groups.entries()) {
        group.sort(stableEventSort);
        for (let index = 0; index < group.length; index += 1) {
          const evt = group[index];
          const evtDate = evt.dateObj || toJsDate(evt.date) || new Date(0);
          const ym = evt.yearMonth || toYearMonth(evtDate);
          const seq = index + 1;
          const name = evt.productName || "Product";
          const code = `${name}-${monthShort(evtDate)}-${seq}`;
          computedByEventId.set(evt.id, { yearMonth: ym, batchCode: code });
        }
      }

      const batchCodeOps = [];
      let updatedEvents = 0;
      for (const evt of eventsWithDates) {
        const computed = computedByEventId.get(evt.id);
        if (!computed) continue;

        const needsUpdate = evt.yearMonth !== computed.yearMonth || evt.batchCode !== computed.batchCode;
        if (!needsUpdate) continue;

        batchCodeOps.push((batch) => {
          batch.update(evt.ref, {
            yearMonth: computed.yearMonth,
            batchCode: computed.batchCode
          });
        });
        updatedEvents += 1;
      }

      if (batchCodeOps.length > 0) {
        await commitOps(batchCodeOps, "Updating production events");
      }

      // 2.5) Sync existing usage rows' batchCode/yearMonth to match production events
      setMigrationStatus("Syncing existing usage rows batch codes...");
      try {
        const usageSnap = await getDocs(collection(db, "ingredient_usage_history"));
        const usageDocs = usageSnap.docs.map(d => ({ id: d.id, ref: d.ref, ...d.data() }));

        const usageUpdateOps = [];
        let updatedUsageRows = 0;
        for (const row of usageDocs) {
          const evtId = row.productionEventId;
          if (!evtId) continue;
          const computed = computedByEventId.get(evtId);
          if (!computed) continue;

          const needsUpdate = row.batchCode !== computed.batchCode || row.yearMonth !== computed.yearMonth;
          if (!needsUpdate) continue;

          usageUpdateOps.push((batch) => {
            batch.update(row.ref, { batchCode: computed.batchCode, yearMonth: computed.yearMonth });
          });
          updatedUsageRows += 1;
        }

        if (usageUpdateOps.length > 0) {
          await commitOps(usageUpdateOps, "Updating usage rows");
        }
        setMigrationStatus(`Batch codes recomputed. Events updated: ${updatedEvents}. Usage rows updated: ${updatedUsageRows}. Continuing FIFO backfill...`);
      } catch (usageSyncErr) {
        console.warn("Usage-row batch code sync failed; continuing:", usageSyncErr);
      }

      // 3) Backfill FIFO usage rows by replaying production events chronologically
      setMigrationStatus("Backfilling FIFO ingredient usage history...");
      const usageOps = [];
      let createdUsageRows = 0;
      let migratedEvents = 0;

      // Ensure lots are sufficient to cover historical usage + current stock.
      // This allows backfilling even if purchase history/lots were never tracked before.
      const totalUsageNeededByIngredientId = new Map();
      for (const evt of eventsWithDates) {
        if (!Array.isArray(evt.ingredients) || evt.ingredients.length === 0) continue;
        for (const ing of evt.ingredients) {
          const ingName = String(ing?.name || "").toLowerCase();
          const resolvedIngredient = ing.ingredientId
            ? ingredientById.get(ing.ingredientId)
            : ingredientByName.get(ingName);
          if (!resolvedIngredient?.id) continue;

          const stockUnit = resolvedIngredient.unit || ing.stockUnit || ing.unit || "units";
          const qtyStock = (ing.quantityStockUnit !== undefined && ing.quantityStockUnit !== null)
            ? Number(ing.quantityStockUnit)
            : Number(convertUnits(ing.quantity || 0, ing.unit || stockUnit, stockUnit));
          const required = Math.round(qtyStock * 1000) / 1000;
          if (!Number.isFinite(required) || required <= 0) continue;

          totalUsageNeededByIngredientId.set(
            resolvedIngredient.id,
            (totalUsageNeededByIngredientId.get(resolvedIngredient.id) || 0) + required
          );
        }
      }

      // Create legacy/top-up lots where needed
      for (const [ingId, totalUsage] of totalUsageNeededByIngredientId.entries()) {
        const ing = ingredientById.get(ingId);
        const currentNow = Number(ing?.currentStock ?? ing?.stock ?? 0) || 0;
        const targetAvailable = Math.round((totalUsage + currentNow) * 1000) / 1000;
        const lots = lotsByIngredientId.get(ingId) || [];
        const availableNow = Math.round(lots.reduce((sum, l) => sum + (Number(l.remainingQuantity) || 0), 0) * 1000) / 1000;
        const topUp = Math.round((targetAvailable - availableNow) * 1000) / 1000;

        if (topUp > 0) {
          const legacyRef = doc(collection(db, "ingredient_inventory_lots"));
          const legacyLot = normalizeLot({
            id: legacyRef.id,
            ref: legacyRef,
            ingredientId: ingId,
            ingredientName: ing?.name || null,
            unit: ing?.unit || null,
            purchaseDate: new Date(0),
            quantityPurchased: topUp,
            remainingQuantity: topUp,
            isLegacy: true
          });
          lots.push(legacyLot);
          lots.sort((a, b) => a.purchaseDate.getTime() - b.purchaseDate.getTime());
          lotsByIngredientId.set(ingId, lots);

          const legacyPayload = {
            ingredientId: ingId,
            ingredientName: ing?.name || null,
            unit: ing?.unit || null,
            purchaseDate: new Date(0),
            quantityPurchased: topUp,
            remainingQuantity: topUp,
            isLegacy: true,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          usageOps.push((batch) => {
            batch.set(legacyRef, legacyPayload);
          });
          createdLots += 1;
        }
      }

      const consumeFromLots = (ingId, qtyNeeded) => {
        const lots = lotsByIngredientId.get(ingId) || [];
        let remaining = Number(qtyNeeded) || 0;
        const allocations = [];
        for (const lot of lots) {
          if (remaining <= 0) break;
          const lotRem = Number(lot.remainingQuantity) || 0;
          if (lotRem <= 0) continue;
          const used = Math.min(lotRem, remaining);
          lot.remainingQuantity = Math.round((lotRem - used) * 1000) / 1000;
          remaining = Math.round((remaining - used) * 1000) / 1000;
          allocations.push({ lot, used });
        }
        return { allocations, remainingUnfilled: remaining };
      };

      for (const evt of eventsWithDates) {
        if (evt.fifoMigrated) continue;
        if (!Array.isArray(evt.ingredients) || evt.ingredients.length === 0) continue;

        const evtDate = evt.dateObj || new Date(0);
        const computed = computedByEventId.get(evt.id);
        const ym = computed?.yearMonth || evt.yearMonth || toYearMonth(evtDate);
        const code = computed?.batchCode || evt.batchCode || `${evt.productName || "Product"}-${monthShort(evtDate)}-1`;

        for (const ing of evt.ingredients) {
          const ingName = String(ing?.name || "").toLowerCase();
          const resolvedIngredient = ing.ingredientId
            ? ingredientById.get(ing.ingredientId)
            : ingredientByName.get(ingName);
          if (!resolvedIngredient?.id) continue;

          const stockUnit = resolvedIngredient.unit || ing.stockUnit || ing.unit || "units";
          const qtyStock = (ing.quantityStockUnit !== undefined && ing.quantityStockUnit !== null)
            ? Number(ing.quantityStockUnit)
            : Number(convertUnits(ing.quantity || 0, ing.unit || stockUnit, stockUnit));

          const required = Math.round(qtyStock * 1000) / 1000;
          if (!Number.isFinite(required) || required <= 0) continue;

          // Lots are pre-top-upped above; no-op here.

          const { allocations } = consumeFromLots(resolvedIngredient.id, required);
          for (const { lot, used } of allocations) {
            const usageRef = doc(collection(db, "ingredient_usage_history"));
            const lotRef = lot.ref;
            const lotId = lot.id;
            const lotPurchaseDate = lot.purchaseDate || null;
            const purchaseHistoryId = lot.purchaseHistoryId || null;
            const lotRemainingAfter = lot.remainingQuantity;
            const payload = {
              ingredientId: resolvedIngredient.id,
              ingredientName: resolvedIngredient.name,
              unit: stockUnit,
              quantityUsed: used,
              date: evtDate,
              productId: evt.productId || null,
              productName: evt.productName || null,
              recipeName: evt.recipeName || null,
              quantityProduced: evt.quantityProduced ?? null,
              productionEventId: evt.id,
              batchCode: code,
              yearMonth: ym,
              lotId,
              lotPurchaseDate,
              purchaseHistoryId,
              lotRemainingAfter,
              createdAt: new Date()
            };
            usageOps.push((batch) => {
              batch.set(usageRef, payload);
              if (lotRef) {
                batch.update(lotRef, { remainingQuantity: lotRemainingAfter, updatedAt: new Date() });
              }
            });
            createdUsageRows += 1;
          }
        }

        usageOps.push((batch) => {
          batch.update(evt.ref, { fifoMigrated: true, yearMonth: ym, batchCode: code });
        });
        migratedEvents += 1;
      }

      if (usageOps.length > 0) {
        await commitOps(usageOps, "Writing usage history");
      }

      setMigrationStatus(
        `Migration complete. Created lots: ${createdLots}. Updated events: ${updatedEvents}. Migrated events: ${migratedEvents}. Usage rows: ${createdUsageRows}.`
      );
    } catch (err) {
      console.error("Migration failed:", err);
      alert(`Migration failed: ${err.message}`);
      setMigrationStatus("Migration failed. See console for details.");
    } finally {
      setMigrationRunning(false);
    }
  };

  const dedupeIngredientUsage = async () => {
    const ok = window.confirm(
      "Deduplicate ingredient usage rows by productionEvent+ingredient+lot (merge quantities) and recompute lot balances?\n\nThis is optional and intended for cleaning up historical duplicates."
    );
    if (!ok) return;

    setMigrationRunning(true);
    setMigrationStatus("Scanning usage rows...");

    const commitOps = async (ops, label) => {
      const CHUNK = 450;
      let committed = 0;
      for (let i = 0; i < ops.length; i += CHUNK) {
        const batch = writeBatch(db);
        ops.slice(i, i + CHUNK).forEach(op => op(batch));
        await batch.commit();
        committed += Math.min(CHUNK, ops.length - i);
        setMigrationStatus(`${label} (${committed}/${ops.length})...`);
      }
    };

    const toNumber = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    try {
      const usageSnap = await getDocs(collection(db, "ingredient_usage_history"));
      const groups = new Map();

      usageSnap.docs.forEach(d => {
        const data = d.data() || {};
        const lotId = data.lotId;
        const eventId = data.productionEventId;
        const ingredientId = data.ingredientId;
        if (!lotId || !eventId || !ingredientId) return;
        const key = `${eventId}::${ingredientId}::${lotId}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ id: d.id, ref: d.ref, data });
      });

      const dedupeOps = [];
      const mergedUsageByLot = new Map();

      for (const [, rows] of groups.entries()) {
        if (rows.length === 1) {
          const row = rows[0];
          mergedUsageByLot.set(row.data.lotId, (mergedUsageByLot.get(row.data.lotId) || 0) + toNumber(row.data.quantityUsed));
          continue;
        }

        const sumUsed = Math.round(rows.reduce((sum, r) => sum + toNumber(r.data.quantityUsed), 0) * 1000) / 1000;
        const minRemaining = rows.reduce((min, r) => {
          const val = Number(r.data.lotRemainingAfter);
          return Number.isFinite(val) ? Math.min(min, val) : min;
        }, Number.POSITIVE_INFINITY);

        const keep = rows[0];
        dedupeOps.push(batch => batch.update(keep.ref, {
          quantityUsed: sumUsed,
          lotRemainingAfter: Number.isFinite(minRemaining) ? minRemaining : keep.data.lotRemainingAfter ?? null
        }));
        rows.slice(1).forEach(r => dedupeOps.push(batch => batch.delete(r.ref)));

        mergedUsageByLot.set(keep.data.lotId, (mergedUsageByLot.get(keep.data.lotId) || 0) + sumUsed);
      }

      if (dedupeOps.length > 0) {
        await commitOps(dedupeOps, "Deduping usage rows");
      }

      // Recompute lot remainingQuantity = quantityPurchased - totalUsed.
      const lotOps = [];
      const lotIds = Array.from(mergedUsageByLot.keys());
      const lotSnaps = await Promise.all(lotIds.map(id => getDoc(doc(db, "ingredient_inventory_lots", id))));
      lotSnaps.forEach((snap, idx) => {
        if (!snap.exists()) return;
        const lotData = snap.data() || {};
        const used = mergedUsageByLot.get(lotIds[idx]) || 0;
        const purchased = toNumber(lotData.quantityPurchased);
        const newRemaining = Math.max(0, Math.round((purchased - used) * 1000) / 1000);
        lotOps.push(batch => batch.update(snap.ref, { remainingQuantity: newRemaining, updatedAt: new Date() }));
      });

      if (lotOps.length > 0) {
        await commitOps(lotOps, "Recomputing lot balances");
      }

      setMigrationStatus("Usage dedupe complete.");
    } catch (err) {
      console.error("Usage dedupe failed:", err);
      alert(`Usage dedupe failed: ${err.message}`);
      setMigrationStatus("Usage dedupe failed. See console.");
    } finally {
      setMigrationRunning(false);
    }
  };

  // Get current user and their role from Firestore
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role);
        } else {
          setUserRole(null);
        }
      } else {
        setUserRole(null);
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch users with role "New"
  useEffect(() => {
    const fetchNewUsers = async () => {
      setLoading(true);
      const q = query(collection(db, "users"), where("role", "==", "new"));
      const snapshot = await getDocs(q);
      setNewUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    fetchNewUsers();
  }, []);

  // Fetch all users
  useEffect(() => {
    const fetchAllUsers = async () => {
      setLoading(true);
      const snapshot = await getDocs(collection(db, "users"));
      setAllUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    fetchAllUsers();
  }, []);

  // Only allow access if user is admin
  if (initialLoading) return <p>Loading...</p>;
  if (!user || !userRole || userRole.toLowerCase() !== "admin") {
    return <p>Access denied. Admins only.</p>;
  }

  // Approve user (change role to "user")
  const approveUser = async (userId) => {
    setApproving(prev => ({ ...prev, [userId]: true }));
    try {
      await updateDoc(doc(db, "users", userId), { role: "user" });
      setNewUsers(prev => prev.filter(u => u.id !== userId));
      setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, role: "user" } : u));
    } catch (err) {
      alert("Failed to approve user: " + err.message);
    }
    setApproving(prev => ({ ...prev, [userId]: false }));
  };

  return (
    <div className={styles.adminDashboardContainer}>
      <h1>Pending User Approvals</h1>
      {loading ? (
        <p>Loading users...</p>
      ) : newUsers.length === 0 ? (
        <p>No users awaiting approval.</p>
      ) : (
        <div className={styles.cardGrid}>
          {newUsers.map(user => (
            <div key={user.id} className={styles.userCard}>
              <div className={styles.cardField}><strong>Email:</strong> {user.email}</div>
              <div className={styles.cardField}>
                <strong>Created At:</strong>{" "}
                {user.createdAt?.toDate
                  ? user.createdAt.toDate().toLocaleString()
                  : ""}
              </div>
              <button
                className={styles.approveButton}
                onClick={() => approveUser(user.id)}
                disabled={approving[user.id]}
              >
                {approving[user.id] ? "Approving..." : "Approve"}
              </button>
            </div>
          ))}
        </div>
      )}

      <h1 style={{ marginTop: "2rem" }}>All Users</h1>
      {loading ? (
        <p>Loading users...</p>
      ) : allUsers.length === 0 ? (
        <p>No users found.</p>
      ) : (
        <div className={styles.cardGrid}>
          {allUsers.map(user => (
            <div key={user.id} className={styles.userCard}>
              <div className={styles.cardField}><strong>Email:</strong> {user.email}</div>
              <div className={styles.cardField}><strong>Role:</strong> {user.role}</div>
              <div className={styles.cardField}>
                <strong>Created At:</strong>{" "}
                {user.createdAt?.toDate
                  ? user.createdAt.toDate().toLocaleString()
                  : ""}
              </div>
            </div>
          ))}
        </div>
      )}

      <h1 style={{ marginTop: "2rem" }}>Data Migration</h1>
      <p>
        Use this once to migrate existing data into FIFO lots + ingredient usage history, and backfill production batch codes.
      </p>
      <button
        className={styles.approveButton}
        onClick={runFifoMigration}
        disabled={migrationRunning}
      >
        {migrationRunning ? "Migrating..." : "Run FIFO Migration"}
      </button>
      <h2 style={{ marginTop: "1.5rem" }}>Usage Cleanup</h2>
      <p>Deduplicate ingredient usage rows and recompute lot balances.</p>
      <button
        className={styles.approveButton}
        onClick={dedupeIngredientUsage}
        disabled={migrationRunning}
      >
        {migrationRunning ? "Working..." : "Dedupe Usage Rows"}
      </button>
      {migrationStatus && <p style={{ marginTop: "0.75rem" }}>{migrationStatus}</p>}
    </div>
  );
}
